<template>
  <div>
    <organism-dashboard
      v-if="!isLoading"
      :criticalBackupList="criticalBackupList"
      :veeamStatusFields="veeamStatusFields"
      :veeamStatusList="veeamStatusList"
      :latestSuccessfulBackupList="latestSuccessfulBackupList"
      :next6BackupList="next6BackupList"
      :storageUsageList="storageUsageList"
    ></organism-dashboard>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import mixins from "@/mixins"
import mixinsDateTime from "@/mixins/mixinsDatetime"
import { mapActions, mapGetters } from "vuex"
import { getDashboard } from "@/api/api-module3"
import OrganismDashboard from "@/components/organisms/backupManagement/OrganismDashboard.vue"

export default {
  name: "Dashboard",
  components: { OrganismDashboard },
  mixins: [mixins, mixinsDateTime],
  data() {
    return {
      dateTimeRange: {
        from: "",
        to: "",
      },
      intervalId: 0,

      // critical backup
      criticalBackupList: null,

      // veeam status
      veeamStatusFields: [
        {
          key: "name",
          label: "VEEAM",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "ipAddress",
          label: "IP ADDRESS",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "lastStatus",
          label: "Last backup status",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "description",
          label: "Description",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
      ],
      veeamStatusList: null,

      // latest successful backup
      latestSuccessfulBackupList: null,

      // next 6 backup
      next6BackupList: null,

      // storage usage
      storageUsageList: null,
    }
  },
  computed: {
    ...mapGetters("appAutoRefresh", ["getBMMDashboardAutoRefresh"]),

    isLoading() {
      return (
        !this.criticalBackupList ||
        !this.veeamStatusList ||
        !this.latestSuccessfulBackupList ||
        !this.next6BackupList ||
        !this.storageUsageList
      )
    },
    isEnableAutoRefresh() {
      return this.getBMMDashboardAutoRefresh
    },
  },
  watch: {
    isEnableAutoRefresh: {
      deep: true,
      immediate: true,
      handler() {
        if (this.isEnableAutoRefresh) {
          this.intervalId = window.setInterval(() => {
            this.getDataFromApi()
          }, 60000)
        } else {
          window.clearInterval(this.intervalId)
        }
      },
    },
  },
  async created() {
    this.setIsShowAutoRefresh(true)
    await this.getDataFromApi()
  },
  beforeDestroy() {
    this.setIsShowAutoRefresh(false)
    window.clearInterval(this.intervalId)
  },
  methods: {
    ...mapActions("appAutoRefresh", ["setIsShowAutoRefresh"]),

    async getDataFromApi() {
      await this.showLoading(true)

      this.criticalBackupList = []
      this.veeamStatusList = []
      this.latestSuccessfulBackupList = []
      this.next6BackupList = []
      this.storageUsageList = []

      await getDashboard()
        .then(
          ({
            criticalBackup,
            veeamStatus,
            successfulBackup,
            nextBackup,
            storageUsage,
          }) => {
            Promise.all([
              this.getCriticalBackupListFromApi(criticalBackup),
              this.getVeeamStatusListFromApi(veeamStatus),
              this.getLatestSuccessfulBackupListFromApi(successfulBackup),
              this.getNext6BackupListFromApi(nextBackup),
              this.getStorageUsageListFromApi(storageUsage),
            ])
          }
        )
        .finally(() => this.showLoading(false))
    },

    getCriticalBackupListFromApi(criticalBackupList) {
      criticalBackupList.forEach((criticalBackup) => {
        this.criticalBackupList.push({
          status: criticalBackup.status,
          id: criticalBackup.id,
          name: criticalBackup.name,
          description: criticalBackup.description,
          ipAddress: criticalBackup.virtualMachines.map((vm) => vm.hostname),
          veeamRef: criticalBackup.veeamName,
          eventDate: {
            date: DateTime.fromISO(criticalBackup.timestamp).toFormat(
              "dd/MM/yyyy"
            ),
            time: DateTime.fromISO(criticalBackup.timestamp).toFormat(
              "HH:mm:ss"
            ),
          },
        })
      })
    },

    getVeeamStatusListFromApi(veeamStatusList) {
      veeamStatusList.forEach((veeamStatus) => {
        this.veeamStatusList.push({
          id: veeamStatus.id,
          name: veeamStatus.name,
          ipAddress: veeamStatus.ipAddress,
          lastStatus: {
            success: veeamStatus.lastBackup.success,
            warning: veeamStatus.lastBackup.warning,
            critical: veeamStatus.lastBackup.critical,
          },
          description: veeamStatus.description,
        })
      })
    },

    getLatestSuccessfulBackupListFromApi(successfulBackupList) {
      successfulBackupList.forEach((successfulBackup) => {
        this.latestSuccessfulBackupList.push({
          id: successfulBackup.id,
          name: successfulBackup.name,
          ipAddress: successfulBackup.virtualMachines.map((vm) => vm.hostname),
          veeamRef: successfulBackup.veeamName,
          eventDate: {
            date: successfulBackup.timestamp
              ? DateTime.fromISO(successfulBackup.timestamp).toFormat(
                  "dd/MM/yyyy"
                )
              : null,
            time: successfulBackup.timestamp
              ? DateTime.fromISO(successfulBackup.timestamp).toFormat(
                  "HH:mm:ss"
                )
              : null,
          },
        })
      })
    },

    getNext6BackupListFromApi(nextBackupList) {
      nextBackupList.forEach((nextBackup) => {
        this.next6BackupList.push({
          id: nextBackup.id,
          name: nextBackup.name,
          ipAddress: nextBackup.virtualMachines.map((vm) => vm.hostname),
          veeamRef: nextBackup.veeamName,
          eventDate: {
            date: nextBackup.timestamp
              ? DateTime.fromISO(nextBackup.timestamp).toFormat("dd/MM/yyyy")
              : null,
            time: nextBackup.timestamp
              ? DateTime.fromISO(nextBackup.timestamp).toFormat("HH:mm:ss")
              : null,
          },
        })
      })
    },

    getStorageUsageListFromApi(storageUsageList) {
      storageUsageList.forEach((storageUsage) => {
        this.storageUsageList.push({
          id: storageUsage.id,
          name: storageUsage.name,
          veeamRef: storageUsage.description,
          usage:
            storageUsage.usagePercent > 0
              ? storageUsage.usagePercent.toFixed(2)
              : 0,
          usageGb: storageUsage.usageGb,
          totalGb: storageUsage.totalGb,
        })
      })
    },
  },
}
</script>

<style scoped></style>
